import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';

/**
 * Globalny kontekst masowego zatwierdzania faktur zakupu (generowanie PZ).
 *
 * Stan i sama petla zyja TUTAJ (provider zamontowany ponad routingiem), a nie
 * w komponencie listy faktur. Dzieki temu operacja i pasek postepu przetrwaja
 * wyjscie ze strony i powrot — pasek renderowany jest globalnie w Layout, wiec
 * jest widoczny na kazdej podstronie przez cala operacje.
 */
const BulkApprovalContext = createContext(null);

export const useBulkApproval = () => {
  const ctx = useContext(BulkApprovalContext);
  if (!ctx) throw new Error('useBulkApproval must be used within BulkApprovalProvider');
  return ctx;
};

export const BulkApprovalProvider = ({ children }) => {
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(0);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState('');
  // Licznik zakonczonych operacji — strony moga na niego reagowac (useEffect),
  // zeby odswiezyc liste po zakonczeniu zatwierdzania.
  const [completedTick, setCompletedTick] = useState(0);
  const runningRef = useRef(false);

  const startBulkApproval = useCallback(async (items, locationId) => {
    // items: [{ id, numer }]
    if (runningRef.current) {
      toast.error('Zatwierdzanie juz trwa — poczekaj na zakonczenie.');
      return;
    }
    if (!items || items.length === 0) return;
    if (!locationId) {
      toast.error('Brak wybranej lokalizacji — nie mozna wygenerowac PZ');
      return;
    }

    runningRef.current = true;
    setRunning(true);
    setTotal(items.length);
    setDone(0);
    setCurrent('');

    let ok = 0;
    const errors = [];

    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      setCurrent(it.numer || `#${it.id}`);
      try {
        // Backend idempotentny — przy retry zwraca istniejace PZ.
        const response = await api.post(`/warehouse/external-receipt/${it.id}`, {
          warehouse_id: locationId,
          location_id: locationId,
        }, { timeout: 180000 });
        if (response.data?.success) {
          ok++;
        } else {
          errors.push(`#${it.id}: ${response.data?.error || response.data?.message || 'nieznany blad'}`);
        }
      } catch (err) {
        errors.push(`#${it.id}: ${err?.message || 'blad polaczenia'}`);
      }
      setDone(i + 1);
    }

    setCurrent('');
    if (errors.length === 0) {
      toast.success(`Zatwierdzono ${ok}/${items.length} — PZ wygenerowane`);
    } else if (ok > 0) {
      toast(`Zatwierdzono ${ok}/${items.length}. Bledy (${errors.length}):\n${errors.slice(0, 5).join('\n')}`, {
        icon: '⚠️', duration: 8000,
      });
    } else {
      toast.error(`Nie udalo sie zatwierdzic zadnej faktury.\n${errors.slice(0, 5).join('\n')}`, { duration: 8000 });
    }

    runningRef.current = false;
    setRunning(false);
    setTotal(0);
    setDone(0);
    setCompletedTick((t) => t + 1);
  }, []);

  const value = {
    running, done, total, current, completedTick,
    progressPct: total ? Math.round((done / total) * 100) : 0,
    startBulkApproval,
  };

  return (
    <BulkApprovalContext.Provider value={value}>
      {children}
    </BulkApprovalContext.Provider>
  );
};

export default BulkApprovalContext;
